import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  Observable,
  Subject,
  takeUntil,
} from 'rxjs';
import { getPaginationArray, PAGINATION_DOTS } from '../../utils/get-pagination-array';
import { BoschUiPaginatorConfigType } from '../../basics/bosch-ui-paginator/bosch-ui-paginator.model';
import { BoschUiPageEvent } from '../../basics/bosch-ui-paginator/bosch-ui-paginator.model';
import { BoschUiPaginatorBase } from '../../basics/bosch-ui-paginator/bosch-ui-paginator-base';

@Component({
  selector: 'bosch-ui-paginator',
  imports: [CommonModule],
  templateUrl: './bosch-ui-paginator.component.html',
  styleUrls: ['./bosch-ui-paginator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
// TODO: create base classes for client and server pagination. check ListClientPagination
export class BoschUiPaginatorComponent extends BoschUiPaginatorBase implements OnInit, OnDestroy {
  @Input() override set totalElements(totalElements: number) {
    this.totalElements$.next(totalElements);
    this.paginatorState = {
      ...this.paginatorState,
      totalElements,
    };
  }
  @Input() override set pageSize(pageSize: number) {
    this.pageSize$.next(pageSize);
    this.pageNumber$.next(0);
    this.paginatorState = {
      ...this.paginatorState,
      pageSize,
    };
  }
  @Input() override set pageNumber(pageNumber: number) {
    this.pageNumber$.next(pageNumber);
    this.paginatorState = {
      ...this.paginatorState,
      pageNumber,
    };
  }
  @Input() set maxVisiblePages(maxVisiblePages: number) {
    this.maxVisiblePages$.next(maxVisiblePages);
  }
  @Input() disabled = false;
  @Output() page = new EventEmitter<BoschUiPageEvent>();

  readonly PAGINATION_DOTS = PAGINATION_DOTS;
  config$: Observable<BoschUiPaginatorConfigType | null>;

  private _config$ = new BehaviorSubject<BoschUiPaginatorConfigType | null>(null);
  private _unsubscribe$ = new Subject<void>();

  constructor() {
    super();
    this.config$ = this._config$.asObservable();
  }

  ngOnInit(): void {
    this.initPaginatorConfigSubscription();
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
  }

  onPageClick(page: number | string): void {
    if (typeof page === 'number') {
      this.pageNumber$.next(page);
    }
  }

  trackByFn(index: number): string {
    return 'pagination_' + index;
  }

  private getPaginationConfig(
    totalElements: number,
    pageSize: number,
    pageNumber: number,
    previousPageNumber: number,
    maxVisiblePages: number,
  ): BoschUiPaginatorConfigType {
    const totalPages = Math.ceil(totalElements / pageSize);
    const pages = getPaginationArray(pageNumber, totalPages, 1, maxVisiblePages);

    return {
      totalElements,
      pageSize,
      pageNumber,
      previousPageNumber,
      totalPages,
      pages,
      hasPreviousPage: pageNumber > 0,
      hasNextPage: pageNumber < totalPages - 1,
    };
  }

  private initPaginatorConfigSubscription(): void {
    combineLatest([
      this.totalElements$.pipe(distinctUntilChanged()),
      this.pageSize$.pipe(distinctUntilChanged()),
      this.pageNumber$.pipe(distinctUntilChanged()),
      this.maxVisiblePages$.pipe(distinctUntilChanged()),
    ])
      .pipe(debounceTime(0), takeUntil(this._unsubscribe$))
      .subscribe(([totalElements, pageSize, pageNumber, maxVisiblePages]: [number, number, number, number]): void => {
        const previousPageNumber = this.paginatorState.pageNumber;
        const config = this.getPaginationConfig(
          totalElements,
          pageSize,
          pageNumber,
          previousPageNumber,
          maxVisiblePages,
        );

        this.paginatorState = {
          totalElements: config.totalElements,
          pageNumber: config.pageNumber,
          pageSize: config.pageSize,
          previousPageNumber,
        };

        this._config$.next(config);
        this.page.next(this.paginatorState);
        this.page$.next(this.paginatorState);
      });
  }
}
